import { Tooltip } from 'antd';
import clsx from 'clsx';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { Link } from 'react-router';
import { TenantLogo, TenantMark } from '../../shared/features/organization/components/TenantLogo';
import { useOrganization } from '../../shared/features/organization/components/OrganizationProvider';
import { SidebarNav } from './SidebarNav';
import { NotificationBell } from '../../features/notifications/public/NotificationBell';
import { SidebarFooter } from './SidebarFooter';

interface AppSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

/** Desktop and tablet navigation rail. Phones get MobileNav instead. */
export function AppSidebar({ collapsed, onToggle }: AppSidebarProps) {
  const ToggleIcon = collapsed ? PanelLeftOpen : PanelLeftClose;
  const { name } = useOrganization();

  return (
    <aside
      className={clsx(
        'sticky top-0 flex h-screen shrink-0 flex-col border-r border-line bg-sider transition-[width] duration-200',
        collapsed ? 'w-16' : 'w-60',
      )}
    >
      <div className={clsx('flex h-14 items-center', collapsed ? 'justify-center' : 'justify-between pr-2.5 pl-5')}>
        <Link to="/" aria-label={`${name} home`} className="min-w-0">
          {collapsed ? <TenantMark /> : <TenantLogo size="sidebar" />}
        </Link>
        {!collapsed && (
          <Tooltip title="Collapse sidebar">
            <button
              type="button"
              aria-label="Collapse sidebar"
              onClick={onToggle}
              className="grid size-7 place-items-center rounded-md text-muted transition-colors hover:bg-sider-selected hover:text-fg"
            >
              <ToggleIcon className="size-4" />
            </button>
          </Tooltip>
        )}
      </div>

      <div className="flex-1 overflow-y-auto py-3">
        <SidebarNav collapsed={collapsed} />
      </div>

      <SidebarFooter collapsed={collapsed}>
        <NotificationBell />
        {collapsed && (
          <Tooltip title="Expand sidebar" placement="right">
            <button
              type="button"
              aria-label="Expand sidebar"
              onClick={onToggle}
              className="grid size-7 place-items-center rounded-md text-muted transition-colors hover:bg-sider-selected hover:text-fg"
            >
              <ToggleIcon className="size-4" />
            </button>
          </Tooltip>
        )}
      </SidebarFooter>
    </aside>
  );
}
